/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import type { ArtifactSteward } from '@artifact-stewardship';
import type { TransferAccessChange, TransferImpactDto } from '@transfer-impact';

/**
 * **The sentences a transfer's confirm says** (`OT3`, slice `3d`, `#900`).
 *
 * One place, for every artifact type and every surface: the server states facts per audience and
 * this turns them into the consequence — *who can see and change this afterwards* — rather than
 * restating the two fields (`#900` R8: *"state the resulting access rather than restate the two
 * fields"*). A pure function so the words are tested without a DOM.
 */

export interface TransferStatement {
  title: string;
  lines: string[];
  /** `danger` when somebody loses something; a transfer that only grants is `neutral`. */
  tone: 'danger' | 'neutral';
  confirmLabel: string;
}

/** What to call each audience in a sentence. People are named; the rest are groups. */
function subject(change: TransferAccessChange): string {
  switch (change.audience) {
    case 'previous-owner':
    case 'new-owner':
      return change.label ?? 'The owner';
    case 'ad-ops':
      return 'Ad ops users';
    case 'everyone-else':
      return 'Other users';
  }
}

/** One sentence for an audience, or none when nothing changes for it. */
export function describeAccessChange(change: TransferAccessChange): string | null {
  const who = subject(change);
  const { see, change: edit } = change;
  const gainsSee = !see.before && see.after;
  const losesSee = see.before && !see.after;
  const gainsEdit = !edit.before && edit.after;
  const losesEdit = edit.before && !edit.after;
  if (losesSee && losesEdit) return `${who} will no longer be able to see or change it.`;
  if (losesSee) return `${who} will no longer be able to see it.`;
  if (losesEdit) return `${who} will still see it, and will no longer be able to change it.`;
  if (gainsSee && gainsEdit) return `${who} will be able to see and change it.`;
  if (gainsEdit) return `${who} will be able to change it.`;
  if (gainsSee) return `${who} will be able to see it.`;
  return null;
}

/**
 * The whole confirm. `targetLabel` is what the operator picked, by name; `alsoPublic` says whether
 * the impact was fetched with *also make it public* (`OT4`), so the reach sentence can say which.
 */
export function describeTransfer(
  impact: TransferImpactDto,
  targetLabel: string,
  alsoPublic: boolean,
): TransferStatement {
  const toSystem = impact.to.kind === 'system';
  const lines: string[] = [];

  for (const change of impact.access) {
    const sentence = describeAccessChange(change);
    if (sentence) lines.push(sentence);
  }

  const losing = impact.referencesLosingSight;
  if (losing && losing.count > 0) {
    const owner = impact.access.find((a) => a.audience === 'previous-owner')?.label ?? 'The previous owner';
    const more = losing.count > losing.names.length ? ` and ${losing.count - losing.names.length} more` : '';
    const noun = losing.count === 1 ? 'artifact' : 'artifacts';
    lines.push(
      `${owner} has ${losing.count} ${noun} that point at it and will no longer be able to open it: ` +
        `${losing.names.join(', ')}${more}.`,
    );
  }

  if (alsoPublic) {
    lines.push('It will be made public first, so every signed-in user can see it.');
  } else if (impact.visibility === 'private' && toSystem) {
    // Measured, not assumed: a private artifact nobody owns is seen by admins alone (`OT4`).
    lines.push('It stays private, so only admins will be able to see it.');
  }

  if (impact.actorLosesChange) {
    lines.push('You will not be able to change it afterwards, or take it back.');
  }

  lines.push('This is recorded in the audit log.');

  const someoneLoses =
    impact.actorLosesChange ||
    impact.access.some((a) => (a.see.before && !a.see.after) || (a.change.before && !a.change.after));

  return {
    title: transferTitle(impact.to, targetLabel),
    lines,
    tone: someoneLoses ? 'danger' : 'neutral',
    confirmLabel: transferConfirmLabel(impact.to),
  };
}

/** The question, which is known before its consequence is. */
export function transferTitle(target: ArtifactSteward, targetLabel: string): string {
  return target.kind === 'system' ? 'Promote this to system?' : `Transfer this to ${targetLabel}?`;
}

/** The confirm button names the act, and the act is known before its consequence is. */
export function transferConfirmLabel(target: ArtifactSteward): string {
  return target.kind === 'system' ? 'Promote to system' : 'Transfer';
}

/** A picker value for a steward — `system`, or `user:<id>`. */
export function stewardValue(steward: ArtifactSteward): string {
  return steward.kind === 'system' ? 'system' : `user:${steward.userId}`;
}

export function stewardFromValue(value: string): ArtifactSteward | null {
  if (value === 'system') return { kind: 'system' };
  if (value.startsWith('user:') && value.length > 5) return { kind: 'user', userId: value.slice(5) };
  return null;
}
